import { apiFetch } from "../../lib/http";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PageShell } from "../../components/layout/PageShell";
import { Chip } from "../../components/ui/Chip";
import { AccountingEntry, BankId, Currency, DataType, EntryStatus, Metal, CompleteEntry, EntryLine } from "../../types";
import { ToolbarFilters } from "./components/ToolbarFilters";
import { BulkBar } from "./components/BulkBar";
import { AccountingTable } from "./components/AccountingTable";
import { Skeleton } from "../../components/ui/Skeleton";
import { Modal } from "../../components/ui/Modal";
import { AvailabilityNavCard } from "./components/AvailabilityNavCard";
import { formatEUR, formatOunces } from "../../lib/format";
import toast from "react-hot-toast";
import { Upload, RefreshCw } from "lucide-react";
import { NumericFormat } from 'react-number-format';

const STATUS_CODE_TO_LABEL: Record<string, EntryStatus> = {
  PENDING: "Da processare",
  SENT: "Registra",
  CONFIRMED: "Confermato",
  IGNORE: "Ignora",
};

const STATUS_LABEL_TO_CODE: Record<EntryStatus, string> = {
  "Da processare": "PENDING",
  "Registra": "SENT",
  "Confermato": "CONFIRMED",
  "Ignora": "IGNORE",
};

function mapStatusCodeToLabel(code?: string | null): EntryStatus {
  if (!code) return "Da processare";
  return STATUS_CODE_TO_LABEL[code] ?? "Da processare";
}

function mapStatusLabelToCode(label: EntryStatus): string {
  return STATUS_LABEL_TO_CODE[label] ?? "PENDING";
}

function mapCurrency(c: string | null | undefined): Currency {
  if (!c) return "Euro";
  const u = c.toUpperCase();
  if (u === "EUR") return "Euro";
  if (u === "USD") return "Dollari";
  return "Euro";
}

function mapOpCurrencyFromNote(note?: string | null, fallback: Currency = "Euro"): Currency {
  if (!note) return fallback;
  const n = note.toUpperCase();
  if (n.includes("EUR")) return "Euro";
  if (n.includes("USD")) return "Dollari";
  return fallback;
}

function mapMetalFromNote(note?: string | null): Metal {
  const n = (note || "").toUpperCase();
  if (n.includes("XAU")) return "Oro";
  if (n.includes("XAG")) return "Argento";
  if (n.includes("XPT")) return "Platino";
  if (n.includes("XPD")) return "Palladio";
  if (n.includes("XIR") || n.includes("XRH")) return "Iridio";
  return "Oro";
}

function mapOperationTypeFromCode(code?: string | null, swapLeg?: string | null, opDirection?: string | null): DataType {
  const c = (code || "").toUpperCase();
  if (c.includes("SWAP")) {
    if (swapLeg) {
      const leg = String(swapLeg).toUpperCase();
      if (leg === 'LEG1') return "Swap - I gamba";
      if (leg === 'LEG2') return "Swap - II gamba";
    }
    return "Swap - II gamba";
  }
  if (c.includes("PHYS")) return "Fisico";
  const dir = (opDirection || "").toUpperCase();
  if (dir === 'BUY') return "Trade Buy";
  if (dir === 'SELL') return "Trade Sell";
  return "Trade";
}

// Metadati banche: id stabile, etichetta e asset grafici
const BANKS: { id: BankId; label: string; logo?: string; full?: string }[] = [
  { id: "icbc", label: "ICBC", logo: "/bank_logo/icbc_logo.png", full: "/bank_logo/icbc_full.jpg" },
  { id: "julius", label: "Julius Baer", logo: "/bank_logo/julius_logo.png", full: "/bank_logo/julius_full.jpg" },
  { id: "stonex", label: "StoneX", logo: "/bank_logo/stonex_logo.png", full: "/bank_logo/stonex_full.jpg" },
];

const BANK_ID_SET = new Set<BankId>(BANKS.map((b) => b.id));
const BANK_LABEL_TO_ID: Record<string, BankId> = BANKS.reduce((acc, b) => {
  acc[b.label.toLowerCase()] = b.id;
  return acc;
}, {} as Record<string, BankId>);
const BANK_ID_TO_LABEL: Record<BankId, string> = BANKS.reduce((acc, b) => {
  acc[b.id] = b.label;
  return acc;
}, {} as Record<BankId, string>);
const BANK_ID_TO_ASSETS: Record<BankId, { logo?: string; full?: string }> = BANKS.reduce((acc, b) => {
  acc[b.id] = { logo: b.logo, full: b.full };
  return acc;
}, {} as Record<BankId, { logo?: string; full?: string }>);

function normalizeBankId(input: any): BankId {
  if (!input) return "stonex";
  const s = String(input).trim().toLowerCase();
  if (BANK_ID_SET.has(s as BankId)) return s as BankId;
  if (s in BANK_LABEL_TO_ID) return BANK_LABEL_TO_ID[s];
  if (s.includes("icbc")) return "icbc";
  if (s.includes("julius")) return "julius";
  if (s.includes("stonex")) return "stonex";
  return "stonex";
}

function getBankImageCandidates(bankId: BankId): string[] {
  const base = `/bank_logo/${bankId}`;
  const candidates = [
    `${base}_page.png`,
    `${base}_page.jpg`,
    `${base}_full.png`,
    `${base}_full.jpg`,
    `${base}_logo.png`,
    `${base}_logo.jpg`,
  ];
  const mapped = BANK_ID_TO_ASSETS[bankId] || {};
  const extras = [mapped.full, mapped.logo].filter(Boolean) as string[];
  const unique = Array.from(new Set([...candidates, ...extras]));
  return unique;
}

function BankHeroImage({ bankId }: { bankId: BankId }) {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const candidates = getBankImageCandidates(bankId);
    if (!candidates.length) { setResolvedSrc(null); return; }
    const tryNext = (i: number) => {
      if (i >= candidates.length) { if (!cancelled) setResolvedSrc(null); return; }
      const img = new Image();
      img.onload = () => { if (!cancelled) setResolvedSrc(candidates[i]); };
      img.onerror = () => tryNext(i + 1);
      img.src = candidates[i];
    };
    setResolvedSrc(null);
    tryNext(0);
    return () => { cancelled = true; };
  }, [bankId]);
  if (!resolvedSrc) return null;
  return (
    <img
      src={resolvedSrc}
      alt={BANK_ID_TO_LABEL[bankId]}
      className="max-h-16 max-w-full object-contain select-none pointer-events-none"
    />
  );
}

function groupRowsToEntries(rows: any[]): AccountingEntry[] {
  // Il backend restituisce già 1 riga per scrittura con campi aggregati
  return (rows || []).map((row: any) => {
    const entryId = String(row.entry_id ?? row.id ?? `${row.date}-${row.note}`);
    const numLines: number = Number(row.num_lines ?? 0);
    // Per StoneX USD: total_debit è in USD, total_debit_eur è in EUR. Per altre banche/valute usiamo total_debit_eur se disponibile.
    const totalDebitEur = Number(row.total_debit_eur ?? row.total_debit ?? 0);
    const totalDebitOriginal = Number(row.total_debit ?? 0);
    const amount = totalDebitEur; // valore in EUR per display in modalità EUR

    const fxRate: number | null = row.fx_rate != null ? Number(row.fx_rate) : null;
    const currencyCode = String(row.currency || '').toUpperCase();
    const isUsd = currencyCode === 'USD';
    // Per operazioni USD: amountOriginal è il valore in USD (total_debit dal backend)
    const amountOriginal = isUsd ? Math.abs(totalDebitOriginal) : null;

    const description = [
      row.reason_description,
      row.note,
      numLines > 1 ? `${numLines} movimentazioni` : '1 movimentazione'
    ].filter(Boolean).join(" • ");

    return {
      id: `entry-${entryId}`,
      entryId: row.entry_id ?? null,
      date: (row.date || '').split('T')[0],
      tradeDate: row.trade_date ? (row.trade_date || '').split('T')[0] : (row.date || '').split('T')[0],
      bank: normalizeBankId(row.bank),
      operationType: mapOperationTypeFromCode(row.operation_type, row.swap_leg, row.op_direction),
      currency: mapCurrency(row.currency),
      metal: mapMetalFromNote(row.note),
      opCurrency: mapOpCurrencyFromNote(row.note, mapCurrency(row.currency)),
      ounces: row.ounces != null ? Math.abs(Number(row.ounces)) : null,
      description,
      amount: Math.abs(amount),
      fxRate: fxRate,
      fxRateDate: row.fx_rate_date ? String(row.fx_rate_date).split('T')[0] : null,
      fxRateSource: row.fx_rate_source === 'BDI' || row.fx_rate_source === 'AHR' ? row.fx_rate_source : null,
      amountOriginal,
      status: mapStatusCodeToLabel(row.status),
      source: row.source === 'manual' ? 'manual' : 'file',
      account: (row.accounts || ''),
      accountName: (row.account_names || ''),
    } as AccountingEntry;
  });
}

export default function AccountingPage() {
  const [activeBank, setActiveBank] = useState<BankId>("icbc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [types, setTypes] = useState<DataType[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [opCurrencies, setOpCurrencies] = useState<Currency[]>([]);
  const [metals, setMetals] = useState<Metal[]>([]);
  const [statuses, setStatuses] = useState<EntryStatus[]>([]);

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [entries, setEntries] = useState<AccountingEntry[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AccountingEntry | null>(null);
  const [editingComplete, setEditingComplete] = useState<CompleteEntry | null>(null);
  const [editingLines, setEditingLines] = useState<EntryLine[]>([]);
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [editingHeader, setEditingHeader] = useState<any>(null);
  const [autoRecalcFx, setAutoRecalcFx] = useState<boolean>(true);
  const [refreshingBdi, setRefreshingBdi] = useState(false);
  const lastFxRef = useRef<number | null>(null);

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateSuccess, setGenerateSuccess] = useState<{ count: number; message: string } | null>(null);

  // Sync conferma prime note
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ confirmed: number; still_pending: number } | null>(null);

  const [displayMode, setDisplayMode] = useState<'EUR' | 'ORIGINAL'>('EUR');
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Paginazione
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  // Availability + Upload (ex Dashboard) - minimal integration
  type Availability = {
    bank: string;
    overall: {
      min_value_date: string | null;
      max_value_date: string | null;
      count: number;
    };
    by_op_type: any[];
    by_asset_type: any[];
    by_asset_name: any[];
    by_account: any[];
  };
  
  const [availabilityData, setAvailabilityData] = useState<Availability[] | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [processingMessage, setProcessingMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reloadEntries = React.useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const params: string[] = [];
      params.push(`bank=${encodeURIComponent(activeBank)}`);
      if (dateFrom) params.push(`date_start=${encodeURIComponent(dateFrom.toISOString())}`);
      if (dateTo) {
        const dateToEod = new Date(dateTo);
        dateToEod.setHours(23, 59, 59, 999);
        params.push(`date_end=${encodeURIComponent(dateToEod.toISOString())}`);
      }
      const qs = params.length ? `?${params.join('&')}` : '';
      const res = await apiFetch(`/api/entries/list${qs}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const mapped: AccountingEntry[] = groupRowsToEntries(json.rows || []);
      setEntries(mapped);
    } catch (e: any) {
      if (!silent) setError(e?.message || 'Errore');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeBank, dateFrom, dateTo]);

  const fetchAvailability = React.useCallback(async () => {
    try {
      setAvailabilityLoading(true);
      setAvailabilityError(null);
      const requests = BANKS.map((b) => apiFetch(`/api/availability?bank=${b.id}`));
      const responses = await Promise.allSettled(requests);
      const okResponses = responses
        .filter((r): r is PromiseFulfilledResult<Response> => r.status === 'fulfilled' && r.value.ok)
        .map((r) => r.value);
      if (okResponses.length === 0) throw new Error('Nessun dato disponibile');
      const jsons = await Promise.all(okResponses.map((r) => r.json()));
      // Ordina alfabeticamente per etichetta banca
      jsons.sort((a, b) => (BANK_ID_TO_LABEL[normalizeBankId(a.bank)] || a.bank).localeCompare(BANK_ID_TO_LABEL[normalizeBankId(b.bank)] || b.bank));
      setAvailabilityData(jsons);
    } catch (e: any) {
      setAvailabilityError(e?.message || 'Errore');
    } finally {
      setAvailabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = Array.from(e.target.files || []);
    if (files.length === 0) return;
    try {
      setUploading(true);
      setProcessingMessage('Caricamento file...');
      let ok = 0;
      let fail = 0;
      const failedNames: string[] = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          const res = await apiFetch('/api/upload', { method: 'POST', body: formData });
          if (!res.ok) {
            fail++;
            failedNames.push(file.name);
          } else {
            ok++;
          }
        } catch (_) {
          fail++;
          failedNames.push(file.name);
        }
      }
      if (ok > 0) {
        toast.success(ok === files.length ? `Caricati tutti i ${ok} file` : `Caricati ${ok} file su ${files.length}`);
        setProcessingMessage('Aggiornamento disponibilità dati...');
        await fetchAvailability();
        // Attendi un breve momento per assicurarsi che i dati siano disponibili nel database
        // (importante per Julius che richiede enrichment cross-file)
        setProcessingMessage('Preparazione dati...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        // Genera automaticamente le scritture contabili senza filtri per tutte le banche dopo il caricamento
        // Chiamate in sequenza invece che in parallelo per evitare race condition nella cache AHR
        try {
          const bankIds: BankId[] = BANKS.map(b => b.id);
          const results = [];
          for (const bank of bankIds) {
            const bankLabel = BANK_ID_TO_LABEL[bank] || bank;
            setProcessingMessage(`Processando i file di ${bankLabel}...`);
            // Piccola pausa per permettere al messaggio di essere visualizzato
            await new Promise(resolve => setTimeout(resolve, 100));
            setProcessingMessage(`Generando i dati...`);
            try {
              const result = await generateEntriesWithoutFilters(bank);
              console.log(`Scritture generate con successo per ${bank}:`, result);
              results.push({ status: 'fulfilled', value: result });
            } catch (e) {
              console.error(`Errore nella generazione per ${bank}:`, e);
              results.push({ status: 'rejected', reason: e });
            }
          }
          setProcessingMessage('Aggiornamento scritture...');
          await reloadEntries();
        } catch (e) {
          // Non mostrare errore all'utente, solo in console
          console.error('Errore nella generazione automatica delle scritture:', e);
        }
      }
      if (fail > 0) {
        const sample = failedNames.slice(0, 3).join(', ');
        const suffix = failedNames.length > 3 ? '…' : '';
        toast.error(`Non caricati ${fail} file: ${sample}${suffix}`);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setUploading(false);
      setProcessingMessage(null);
    }
  };

  useEffect(() => {
    (async () => {
      await reloadEntries();
    })();
    return () => {};
  }, [reloadEntries]);

  // Auto-refresh entries ogni 5 secondi (sospeso durante operazioni in corso)
  useEffect(() => {
    const busy = uploading || generating || syncing || modalOpen;
    if (busy) return;
    const id = setInterval(() => {
      reloadEntries(true);
    }, 5000);
    return () => clearInterval(id);
  }, [reloadEntries, uploading, generating, syncing, modalOpen]);

  const clearAll = () => {
    setDateFrom(null);
    setDateTo(null);
    setTypes([]);
    setCurrencies([]);
    setOpCurrencies([]);
    setMetals([]);
    setStatuses([]);
  };

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (e.bank !== activeBank) return false;
      if (dateFrom && new Date(e.date) < dateFrom) return false;
      if (dateTo) {
        const dateToEod = new Date(dateTo);
        dateToEod.setHours(23, 59, 59, 999);
        if (new Date(e.date) > dateToEod) return false;
      }
      if (types.length && !types.includes(e.operationType)) return false;
      if (currencies.length && !currencies.includes(e.currency)) return false;
      if (opCurrencies.length && !opCurrencies.includes(e.opCurrency ?? e.currency)) return false;
      if (metals.length && !metals.includes(e.metal)) return false;
      if (statuses.length && !statuses.includes(e.status)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const inDesc = e.description?.toLowerCase().includes(q) ?? false;
        const inId = e.entryId != null && String(e.entryId).includes(q);
        if (!inDesc && !inId) return false;
      }
      return true;
    });
  }, [entries, activeBank, dateFrom, dateTo, types, currencies, opCurrencies, metals, statuses, searchQuery]);

  // selection helpers
  const toggleRow = (id: string) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  // Clamp della pagina se cambia il numero di righe
  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    if (page > newTotalPages) setPage(newTotalPages);
  }, [filtered.length, pageSize, page]);

  // Reset pagina quando cambiano i filtri principali
  useEffect(() => {
    setPage(1);
  }, [activeBank, dateFrom, dateTo, types, currencies, opCurrencies, metals, statuses, searchQuery]);

  const setRowStatus = (id: string, status: EntryStatus) => {
    // Trova la entry corrispondente per ottenere entry_id
    const entry = entries.find(e => e.id === id);
    if (!entry || !entry.entryId) return;
    
    // Optimistic update
    setEntries((arr) => arr.map((e) => (e.id === id ? { ...e, status } : e)));
    
    const doRequest = async () => {
      try {
        // Aggiorna lo stato di tutte le righe con lo stesso entry_id nel backend
        await apiFetch(`/api/entries/by-entry-id/${encodeURIComponent(entry.entryId!)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: mapStatusLabelToCode(status) })
        });
      } catch {
        // rollback simple
        setEntries((arr) => arr.map((e) => (e.id === id ? { ...e, status: e.status } : e)));
      }
    };
    doRequest();
    if (status === "Confermato") setSelected((s) => ({ ...s, [id]: false }));
  };

  const bulkSet = (status: EntryStatus) => {
    const selectedEntries = filtered.filter((r) => selected[r.id] && r.status !== "Confermato");
    const ids = selectedEntries.map((r) => r.id);
    if (!ids.length) return;
    
    // Optimistic update
    setEntries((arr) => arr.map((e) => (ids.includes(e.id) ? { ...e, status } : e)));
    if (status === "Confermato") {
      setSelected((s) => {
        const copy = { ...s };
        ids.forEach((id) => (copy[id] = false));
        return copy;
      });
    }
    
    // Aggiorna nel backend tutte le scritture selezionate
    const doRequest = async () => {
      try {
        await Promise.all(
          selectedEntries
            .filter(e => e.entryId)
            .map(e => 
              apiFetch(`/api/entries/by-entry-id/${encodeURIComponent(e.entryId!)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: mapStatusLabelToCode(status) })
              })
            )
        );
      } catch (err) {
        console.error('Errore nell\'aggiornamento bulk:', err);
        // TODO: gestire rollback o mostrare errore
      }
    };
    doRequest();
  };

  const addManualEntry = () => {
    const draft: AccountingEntry = {
      id: '',
      entryId: null,
      date: new Date().toISOString().split('T')[0],
      tradeDate: new Date().toISOString().split('T')[0],
      bank: activeBank,
      operationType: "Trade",
      currency: "Euro",
      metal: "Oro",
      description: "",
      amount: 0,
      status: "Da processare",
      source: "manual",
      account: '',
      accountName: '',
    };
    setEditing(draft);
    setEditingComplete(null);
      setEditingLines([
      {
        id: Date.now(),
        entry_id: 0,
        account: '',
        account_name: '',
        debit_amount: 0,
        credit_amount: 0,
        debit_eur: 0,
        credit_eur: 0,
      },
    ]);
    setEditingHeader({
      date: draft.date,
      trade_date: draft.tradeDate,
      fiscal_year: new Date(draft.date).getFullYear(),
      currency: 'EUR',
      reason_code: '',
      reason_description: '',
      ounces: 0,
      note: '',
      fx_rate: null,
      fx_rate_source: null,
      fx_rate_date: ''
    });
    setModalOpen(true);
  };

  const onEditRow = async (entry: AccountingEntry) => {
    if (!entry.entryId) {
      // Entry manuale senza movimentazioni
      setEditing({ ...entry });
      setEditingComplete(null);
      setEditingLines([]);
      setEditingHeader({
        date: entry.date,
        trade_date: entry.tradeDate ?? entry.date,
        fiscal_year: new Date(entry.date).getFullYear(),
        currency: entry.currency === 'Dollari' ? 'USD' : 'EUR',
        reason_code: null,
        reason_description: null,
        ounces: 0,
        note: entry.description ?? '',
        fx_rate: null,
        fx_rate_source: null,
        fx_rate_date: ''
      });
      setModalOpen(true);
      return;
    }

    // Carica i dettagli completi della scrittura
    try {
      setLoadingEntry(true);
      const res = await apiFetch(`/api/entries/by-entry-id/${encodeURIComponent(entry.entryId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: CompleteEntry = await res.json();
      setEditingComplete(data);
      // Assicura che debit_eur e credit_eur siano presenti (fallback ai valori originali se mancanti)
      setEditingLines(data.lines.map((l: EntryLine) => ({
        ...l,
        debit_eur: l.debit_eur ?? l.debit_amount,
        credit_eur: l.credit_eur ?? l.credit_amount,
        account_code: l.account_code ?? l.account,
      })));
      console.log(data.lines);
      setEditingHeader({
        date: (data.entry?.date || '').split('T')[0],
        trade_date: (data.entry?.trade_date || '')?.split('T')[0] || (data.entry?.date || '').split('T')[0],
        fiscal_year: data.entry?.fiscal_year,
        currency: data.entry?.currency,
        reason_code: data.entry?.reason_code ?? '',
        reason_description: data.entry?.reason_description ?? '',
        ounces: data.entry?.ounces ?? 0,
        note: data.entry?.note ?? '',
        fx_rate: data.entry?.fx_rate ?? null,
        fx_rate_source: data.entry?.fx_rate_source ?? null,
        fx_rate_date: (data.entry?.fx_rate_date || '') ? (data.entry?.fx_rate_date || '').split('T')[0] : ''
      });
      lastFxRef.current = (data.entry?.fx_rate ?? null) as any;
      setAutoRecalcFx(true);
      setEditing(entry);
      setModalOpen(true);
    } catch (e) {
      console.error('Errore caricamento scrittura:', e);
      // Fallback: apri modale senza dettagli
      setEditing({ ...entry });
      setEditingComplete(null);
      setEditingLines([]);
      setModalOpen(true);
    } finally {
      setLoadingEntry(false);
    }
  };

  const refreshBdiRate = async () => {
    const curr = String(editingHeader?.currency || '').toUpperCase();
    const valueDate = editingHeader?.date;
    if (!curr || curr === 'EUR' || !valueDate) return;
    setRefreshingBdi(true);
    try {
      const res = await apiFetch(`/api/fx-rate?date=${encodeURIComponent(valueDate)}&currency=${encodeURIComponent(curr)}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(`Tasso BDI non disponibile: ${err.detail || res.statusText}`);
        return;
      }
      const data = await res.json();
      setEditingHeader((h: any) => ({ ...h, fx_rate: data.rate, fx_rate_date: data.date, fx_rate_source: 'BDI' }));
      toast.success(`Tasso BDI: ${data.rate} (${data.date})`);
    } catch {
      toast.error('Errore nel recupero del tasso BDI');
    } finally {
      setRefreshingBdi(false);
    }
  };

  // Ricalcolo dinamico righe sulla variazione del tasso (solo valute != EUR)
  // Quando cambia il tasso, ricalcola debit_amount e credit_amount dalla valuta originale basandosi su debit_eur e credit_eur
  useEffect(() => {
    if (!modalOpen) return;
    const newFx = Number(editingHeader?.fx_rate);
    const curr = String(editingHeader?.currency || '').toUpperCase();
    if (curr === 'EUR') {
      // Se la valuta è EUR, debit_amount = debit_eur e credit_amount = credit_eur
      if (editingLines?.length) {
        setEditingLines((lines) => lines.map(l => ({
          ...l,
          debit_amount: l.debit_eur ?? l.debit_amount,
          credit_amount: l.credit_eur ?? l.credit_amount,
          debit_eur: l.debit_eur ?? l.debit_amount,
          credit_eur: l.credit_eur ?? l.credit_amount,
        })));
      }
      return;
    }
    if (!autoRecalcFx) return;
    if (!newFx || newFx <= 0) return;
    if (!editingLines?.length) return;
    const prevFx = lastFxRef.current;
    if (!prevFx || prevFx <= 0) {
      // Prima volta: assicura che debit_eur e credit_eur siano presenti
      setEditingLines((lines) => lines.map(l => ({
        ...l,
        debit_eur: l.debit_eur ?? Number((l.debit_amount / newFx).toFixed(2)),
        credit_eur: l.credit_eur ?? Number((l.credit_amount / newFx).toFixed(2)),
      })));
      lastFxRef.current = newFx;
      return;
    }
    if (Math.abs(prevFx - newFx) < 1e-9) return;
    // Ricalcola debit_eur e credit_eur basandosi su debit_amount e credit_amount (valori nella valuta originale) e il nuovo tasso
    // debit_eur_new = debit_amount / newFx
    setEditingLines((lines) => lines.map(l => ({
      ...l,
      debit_eur: l.debit_amount > 0 ? Number((l.debit_amount / newFx).toFixed(2)) : 0,
      credit_eur: l.credit_amount > 0 ? Number((l.credit_amount / newFx).toFixed(2)) : 0,
    })));
    lastFxRef.current = newFx;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingHeader?.fx_rate, editingHeader?.currency, autoRecalcFx, modalOpen]);

  const saveEntry = async () => {
    if (!editing) return;
    const payload = {
      date: editing.date,
      trade_date: editing.tradeDate ?? editing.date,
      fiscal_year: new Date(editing.date).getFullYear(),
      note: editing.description,
      currency: editing.currency === 'Dollari' ? 'USD' : 'EUR',
      reason: null,
      reason_description: null,
      ounces: 0,
      account: editing.account ?? null,
      account_name: editing.accountName ?? null,
      debit_amount: editing.amount >= 0 ? editing.amount : 0,
      credit_amount: editing.amount < 0 ? -editing.amount : 0,
      status: mapStatusLabelToCode(editing.status),
      source: editing.source ?? 'manual',
      bank: editing.bank,
      fx_rate: editingHeader?.fx_rate ?? null,
      fx_rate_date: editingHeader?.fx_rate_date || null
    };
    try {
      if (!editing.id) {
        const res = await apiFetch(`/api/entries/manual`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error('HTTP ' + res.status);
      } else {
        await apiFetch(`/api/entries/${encodeURIComponent(editing.id)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }
      await reloadEntries();
      setModalOpen(false);
      setEditing(null);
    } catch (e) {
      // TODO: notify error
    }
  };

  const deleteRow = (id: string) => {
    const entry = entries.find(e => e.id === id);
    if (!entry?.entryId) {
      toast.error('Scrittura non eliminabile: ID mancante');
      return;
    }
    if (entry.status !== "Da processare" && entry.status !== "Ignora") {
      toast.error('Solo scritture in stato "Da processare" o "Ignora" possono essere eliminate');
      return;
    }

    const prev = entries;
    setEntries(arr => arr.filter(e => e.id !== id));
    setSelected(s => { const copy = { ...s }; delete copy[id]; return copy; });

    const doRequest = async () => {
      try {
        const res = await apiFetch(`/api/entries/${encodeURIComponent(entry.entryId!)}`, { method: 'DELETE' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || 'Errore eliminazione');
          setEntries(prev);
        } else {
          toast.success('Scrittura eliminata');
        }
      } catch {
        setEntries(prev);
        toast.error('Errore eliminazione');
      }
    };
    doRequest();
  };

  // Genera scritture senza filtri (solo bank)
  const generateEntriesWithoutFilters = async (bank: BankId) => {
    const url = `/api/entries/generate?bank=${encodeURIComponent(bank)}`;
    try {
      console.log(`Generazione scritture per ${bank} - URL: ${url}`);
      const res = await apiFetch(url, { method: 'POST' });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        const errorMsg = errData.detail || `HTTP ${res.status}`;
        console.error(`Errore nella generazione automatica delle scritture per ${bank}:`, {
          status: res.status,
          statusText: res.statusText,
          error: errorMsg,
          url
        });
        throw new Error(errorMsg);
      }
      const result = await res.json();
      console.log(`Scritture generate con successo per ${bank}:`, result);
      return result;
    } catch (e: any) {
      console.error(`Errore nella generazione automatica delle scritture per ${bank}:`, {
        error: e,
        message: e?.message,
        url
      });
      throw e;
    }
  };

  const generateEntries = async () => {
    try {
      setGenerating(true);
      setGenerateError(null);
      setGenerateSuccess(null);

      // Costruisci i parametri query
      const params: string[] = [];
      params.push(`bank=${activeBank}`);
      if (dateFrom) params.push(`date_start=${encodeURIComponent(dateFrom.toISOString())}`);
      if (dateTo) {
        const dateToEod = new Date(dateTo);
        dateToEod.setHours(23, 59, 59, 999);
        params.push(`date_end=${encodeURIComponent(dateToEod.toISOString())}`);
      }
      
      // Converti i filtri in parametri API
      if (metals.length === 1) {
        const metalMap: Record<Metal, string> = {
          "Oro": "XAU",
          "Argento": "XAG",
          "Platino": "XPT",
          "Palladio": "XPD",
          "Iridio": "XIR"
        };
        const selectedMetal = metals[0];
        if (selectedMetal && metalMap[selectedMetal]) {
          params.push(`metal=${metalMap[selectedMetal]}`);
        }
      }
      if (currencies.length === 1) {
        const currencyCode = currencies[0] === "Dollari" ? "USD" : "EUR";
        params.push(`currency=${currencyCode}`);
      }

      const qs = params.length ? `?${params.join('&')}` : '';
      const res = await apiFetch(`/api/entries/generate${qs}`, { method: 'POST' });
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }

      const result = await res.json();
      // TOAST
      toast.success(result.message || 'Scritture generate con successo');
      // setGenerateSuccess({ 
      //   count: result.entries_saved || 0, 
      //   message: result.message || 'Scritture generate con successo' 
      // });

      // Ricarica le entries dopo la generazione mantenendo i filtri
      await reloadEntries();

      // Nascondi il messaggio di successo dopo 5 secondi
      setTimeout(() => setGenerateSuccess(null), 5000);
    } catch (e: any) {
      setGenerateError(e?.message || 'Errore durante la generazione delle scritture');
    } finally {
      setGenerating(false);
    }
  };

  const syncConfirmed = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const res = await apiFetch('/api/entries/sync-confirmed', { method: 'POST' });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSyncResult({ confirmed: data.confirmed ?? 0, still_pending: data.still_pending ?? 0 });
      if (data.confirmed > 0) {
        toast.success(`${data.confirmed} prima/e nota/e confermata/e dal gestionale`);
      } else {
        toast('Nessuna nuova conferma dal gestionale', { icon: '\u2139\uFE0F' });
      }
      await reloadEntries();
      setTimeout(() => setSyncResult(null), 8000);
    } catch (e: any) {
      toast.error(e?.message || 'Errore sincronizzazione gestionale');
    } finally {
      setSyncing(false);
    }
  };

  const sorted = useMemo(() => {
    if (!dateSortOrder) return filtered;
    return [...filtered].sort((a, b) => {
      const da = new Date(a.date).getTime();
      const db = new Date(b.date).getTime();
      return dateSortOrder === 'asc' ? da - db : db - da;
    });
  }, [filtered, dateSortOrder]);
  const visibleRows = sorted;
  // Calcolo paginazione corrente
  const totalRows = visibleRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const startIndex = (page - 1) * pageSize;
  const pagedRows = visibleRows.slice(startIndex, startIndex + pageSize);
  const selectablePageIds = pagedRows.filter((r) => r.status !== "Confermato").map((r) => r.id);
  const allSelectedPage = selectablePageIds.length > 0 && selectablePageIds.every((id) => selected[id]);
  const toggleAllPage = () => {
    const ids = selectablePageIds;
    const every = ids.every((id) => selected[id]);
    if (every) {
      const copy = { ...selected };
      ids.forEach((id) => delete copy[id]);
      setSelected(copy);
    } else {
      const merged = { ...selected } as Record<string, boolean>;
      ids.forEach((id) => (merged[id] = true));
      setSelected(merged);
    }
  };
  const selectedCount = visibleRows.filter((r) => selected[r.id]).length;

  return (
    <>
      {/* Overlay con spinner durante il caricamento e generazione */}
      {(uploading || processingMessage) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 flex flex-col items-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
            <p className="text-lg font-medium text-gray-900 text-center">
              {processingMessage || 'Caricamento in corso...'}
            </p>
            <p className="text-sm text-gray-500 text-center mt-2">
              Attendi, operazione in corso
            </p>
          </div>
        </div>
      )}
      <PageShell
        title="Contabilità"
        subtitle=""
        actions={(
        <div className="flex items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx,.csv,.pdf"
            multiple
            onChange={handleFileUpload}
            disabled={uploading}
            className="hidden"
            id="file-upload-accounting"
          />
          <label
            htmlFor="file-upload-accounting"
            className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg shadow-sm text-white ${uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Caricamento…' : 'Carica dati'}
          </label>
          <a
            href="/amministrazione/files"
            className="ml-2 inline-flex items-center px-3 py-1.5 border border-slate-200 text-xs font-medium rounded-lg shadow-sm text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            File caricati
          </a>
        </div>
      )}
    >
      <div className="flex gap-4">
        <main className="w-full">
          {/* Nav banche (tabs) + logo banca selezionata */}
          <div className="mb-4 rounded-2xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              {/* logo banca selezionata */}
              <div className="order-1 md:order-2 flex md:justify-end">
                <div className="flex items-center justify-center">
                  <BankHeroImage bankId={activeBank} />
                </div>
              </div>
              {/* tabs banche a sx */}
              <div className="order-2 md:order-1 flex-1">
                {availabilityLoading ? (
                  <div className="flex gap-2 overflow-x-auto md:flex-wrap">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <Skeleton className="h-4 w-24" />
                        <div className="mt-2">
                          <Skeleton className="h-6 w-40" />
                        </div>
                        <div className="mt-3">
                          <Skeleton className="h-5 w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : availabilityError ? (
                  <div className="text-xs text-red-600">{availabilityError}</div>
                ) : availabilityData && (
                  <div className="flex gap-2 overflow-x-auto md:flex-wrap">
                    {availabilityData.map((d) => {
                      const bid = normalizeBankId(d.bank);
                      const label = BANK_ID_TO_LABEL[bid] || d.bank;
                      const logo = BANK_ID_TO_ASSETS[bid]?.logo as string | undefined;
                      return (
                        <AvailabilityNavCard
                          key={bid}
                          availability={d}
                          bankLabel={label}
                          bankLogo={logo}
                          isActive={activeBank === bid}
                          onClick={() => setActiveBank(bid)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
            <ToolbarFilters
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              types={types}
              setTypes={setTypes}
              currencies={currencies}
              setCurrencies={setCurrencies}
              opCurrencies={opCurrencies}
              setOpCurrencies={setOpCurrencies}
              metals={metals}
              setMetals={setMetals}
              statuses={statuses}
              setStatuses={setStatuses}
              clearAll={clearAll}
              addManualEntry={addManualEntry}
              onGenerateEntries={generateEntries}
              generating={generating}
              onSyncConfirmed={syncConfirmed}
              syncing={syncing}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {dateFrom && <Chip onRemove={() => setDateFrom(null)}>Dal {dateFrom.toLocaleDateString("it-IT")}</Chip>}
              {dateTo && <Chip onRemove={() => setDateTo(null)}>Al {dateTo.toLocaleDateString("it-IT")}</Chip>}
              {types.map((t) => (
                <Chip key={t} onRemove={() => setTypes(types.filter((x) => x !== t))}>{t}</Chip>
              ))}
              {currencies.map((c) => (
                <Chip key={c} onRemove={() => setCurrencies(currencies.filter((x) => x !== c))}>{c}</Chip>
              ))}
              {opCurrencies.map((c) => (
                <Chip key={`op-${c}`} onRemove={() => setOpCurrencies(opCurrencies.filter((x) => x !== c))}>{c} (op.)</Chip>
              ))}
              {metals.map((m) => (
                <Chip key={m} onRemove={() => setMetals(metals.filter((x) => x !== m))}>{m}</Chip>
              ))}
              {statuses.map((s) => (
                <Chip key={s} onRemove={() => setStatuses(statuses.filter((x) => x !== s))}>{s}</Chip>
              ))}
            </div>
            {/* Toggle visualizzazione importi */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-gray-500">Visualizza totali:</span>
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  className={`px-2 py-1 text-xs ${displayMode === 'EUR' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                  onClick={() => setDisplayMode('EUR')}
                >
                  EUR
                </button>
                <button
                  type="button"
                  className={`px-2 py-1 text-xs border-l border-gray-200 ${displayMode === 'ORIGINAL' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                  onClick={() => setDisplayMode('ORIGINAL')}
                >
                  Valuta originale
                </button>
              </div>
            </div>
            
            {/* Generate messages */}
            {generateSuccess && (
              <div className="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-800">
                ✓ {generateSuccess.message} ({generateSuccess.count} scritture salvate)
              </div>
            )}
            {syncResult && (
              <div className="mt-3 rounded-lg bg-indigo-50 p-3 text-sm text-indigo-800 flex items-center justify-between">
                <span>
                  {syncResult.confirmed > 0
                    ? `${syncResult.confirmed} prima/e nota/e confermata/e dal gestionale`
                    : 'Nessuna nuova conferma dal gestionale'}
                  {syncResult.still_pending > 0 && ` — ${syncResult.still_pending} ancora in attesa`}
                </span>
                <button
                  onClick={() => setSyncResult(null)}
                  className="text-indigo-600 hover:text-indigo-800 font-bold"
                >
                  ×
                </button>
              </div>
            )}
            {generateError && (
              <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-800 flex items-center justify-between">
                <span>✗ {generateError}</span>
                <button 
                  onClick={() => setGenerateError(null)} 
                  className="text-red-600 hover:text-red-800 font-bold"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {loading && (
            <div className="mt-4 space-y-3">
              <Skeleton className="h-8 w-1/3" />
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          )}

          {!loading && error && (
            <div className="mt-4 text-sm text-red-600">{error}</div>
          )}

          {!loading && !error && (
            <>
              {/* Paginazione: controlli */}
              <div className="mt-4 mb-2 flex items-center justify-between text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <span>Mostra</span>
                  <select
                    className="rounded-md border border-gray-300 px-2 py-1 bg-white"
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>per pagina — {totalRows === 0 ? 0 : (startIndex + 1)}-{Math.min(startIndex + pageSize, totalRows)} di {totalRows}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="px-2 py-1 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                    onClick={() => setPage(1)}
                    disabled={page <= 1}
                    aria-label="Prima pagina"
                  >
                    «
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    aria-label="Pagina precedente"
                  >
                    ‹
                  </button>
                  <span className="px-2">Pagina {page} / {totalPages}</span>
                  <button
                    type="button"
                    className="px-2 py-1 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    aria-label="Pagina successiva"
                  >
                    ›
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                    aria-label="Ultima pagina"
                  >
                    »
                  </button>
                </div>
              </div>
              <BulkBar selectedCount={selectedCount} onSetStatus={(s) => bulkSet(s)} onClearSelection={() => setSelected({})} />

              <AccountingTable
                rows={pagedRows}
                toggleRow={toggleRow}
                allSelected={allSelectedPage}
                toggleAll={toggleAllPage}
                setRowStatus={setRowStatus}
                selectedRows={selected}
                deleteRow={deleteRow}
                onEdit={onEditRow}
                displayMode={displayMode}
                dateSortOrder={dateSortOrder}
                onDateSortChange={setDateSortOrder}
              />
              <Modal
                open={modalOpen}
                onClose={() => { 
                  setModalOpen(false); 
                  setEditing(null); 
                  setEditingComplete(null);
                  setEditingLines([]);
                }}
                title={
                  editing?.id
                    ? (
                      <span className="flex items-baseline gap-2">
                        {editingComplete ? 'Modifica scrittura contabile' : 'Modifica scrittura'}
                        {editing.entryId != null && (
                          <span className="font-mono text-sm font-normal text-gray-400">#{editing.entryId}</span>
                        )}
                      </span>
                    )
                    : 'Nuova scrittura'
                }
              >
                <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
                  {loadingEntry && (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-sm text-gray-500">Caricamento...</p>
                    </div>
                  )}

                  {!loadingEntry && editingLines.length > 0 && (
                    <>
                    {/* Dati generali scrittura - Editabili */}
                    <div className="mb-3 rounded-lg bg-gray-50 p-3">
                      <h3 className="text-xs font-semibold text-gray-700 mb-2">Informazioni generali</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Data valuta</label>
                          <input 
                            type="date" 
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            value={editingHeader?.date || ''}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, date: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Trade date</label>
                          <input 
                            type="date" 
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            value={editingHeader?.trade_date || ''}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, trade_date: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Anno fiscale</label>
                          <input 
                            type="number" 
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            value={editingHeader?.fiscal_year || new Date().getFullYear()}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, fiscal_year: Number(e.target.value) }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Causale</label>
                          <input 
                            type="text" 
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            value={editingHeader?.reason_code || ''}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, reason_code: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Descrizione causale</label>
                          <input 
                            type="text" 
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            value={editingHeader?.reason_description || ''}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, reason_description: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Valuta</label>
                          <input 
                            type="text" 
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            value={editingHeader?.currency || ''}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, currency: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Tasso di cambio
                            {editingHeader?.fx_rate_source && (
                              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded font-normal ${editingHeader.fx_rate_source === 'manuale' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                                {editingHeader.fx_rate_source}
                              </span>
                            )}
                          </label>
                          <input
                            type="number"
                            step="0.0001"
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs"
                            value={editingHeader?.fx_rate ?? ''}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, fx_rate: e.target.value === '' ? null : Number(e.target.value) }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Data tasso di cambio</label>
                          <input 
                            type="date" 
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            value={editingHeader?.fx_rate_date || ''}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, fx_rate_date: e.target.value }))}
                          />
                        </div>
                        {String(editingHeader?.currency || '').toUpperCase() !== 'EUR' && (
                          <div className="col-span-2 flex items-center justify-end">
                            <button
                              type="button"
                              disabled={
                                refreshingBdi ||
                                (editing?.status !== 'Da processare' && editing?.status !== 'Ignora')
                              }
                              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              onClick={refreshBdiRate}
                              title={
                                editing?.status !== 'Da processare' && editing?.status !== 'Ignora'
                                  ? 'Disponibile solo per scritture in stato Da processare o Ignora'
                                  : 'Recupera tasso ufficiale Banca d\'Italia per la data valuta'
                              }
                            >
                              <RefreshCw className={`h-3.5 w-3.5 ${refreshingBdi ? 'animate-spin' : ''}`} />
                              Aggiorna da BDI
                            </button>
                          </div>
                        )}
                        <div className="col-span-2 flex items-center justify-between">
                          <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={autoRecalcFx}
                              onChange={(e) => setAutoRecalcFx(e.target.checked)}
                            />
                            Aggiorna importi con tasso di cambio
                          </label>
                          {!autoRecalcFx && (
                            <button
                              type="button"
                              className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
                              onClick={() => {
                                const newFx = Number(editingHeader?.fx_rate);
                                const curr = String(editingHeader?.currency || '').toUpperCase();
                                if (!newFx || newFx <= 0 || !editingLines?.length) return;
                                if (curr === 'EUR') {
                                  // Se EUR, i valori sono uguali
                                  setEditingLines((lines) => lines.map(l => ({
                                    ...l,
                                    debit_amount: l.debit_eur ?? l.debit_amount,
                                    credit_amount: l.credit_eur ?? l.credit_amount,
                                    debit_eur: l.debit_eur ?? l.debit_amount,
                                    credit_eur: l.credit_eur ?? l.credit_amount,
                                  })));
                                } else {
                                  // Ricalcola debit_amount e credit_amount basandosi su debit_eur e credit_eur
                                  setEditingLines((lines) => lines.map(l => ({
                                    ...l,
                                    debit_eur: l.debit_amount > 0 ? Number((l.debit_amount / newFx).toFixed(2)) : 0,
                                    credit_eur: l.credit_amount > 0 ? Number((l.credit_amount / newFx).toFixed(2)) : 0,
                                  })));
                                }
                                lastFxRef.current = newFx;
                              }}
                            >
                              Ricalcola ora
                            </button>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Once</label>
                          <input 
                            type="text" 
                            inputMode="decimal"
                            pattern="[0-9]*\.?[0-9]*"
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            value={editingHeader?.ounces != null ? formatOunces(editingHeader.ounces) : ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || val === '-') {
                                setEditingHeader((h: any) => ({ ...h, ounces: null }));
                              } else {
                                const num = parseFloat(val);
                                if (!isNaN(num)) {
                                  setEditingHeader((h: any) => ({ ...h, ounces: num }));
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const val = e.target.value;
                              if (val !== '' && val !== '-') {
                                const num = parseFloat(val);
                                if (!isNaN(num)) {
                                  setEditingHeader((h: any) => ({ ...h, ounces: num }));
                                }
                              }
                            }}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Note</label>
                          <textarea 
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" 
                            rows={2}
                            value={editingHeader?.note || ''}
                            onChange={(e) => setEditingHeader((h: any) => ({ ...h, note: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Movimentazioni - Tabella editabile */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-700 mb-2">Movimentazioni ({editingLines.length})</h3>
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <div className="max-h-64 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-100 sticky top-0 z-10">
                              <tr>
                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300 w-20">Conto</th>
                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 border-b border-gray-300">Descrizione</th>
                                <th className="px-2 py-1.5 text-right font-semibold text-green-700 border-b border-gray-300 w-24 bg-green-50">Dare (EUR)</th>
                                <th className="px-2 py-1.5 text-right font-semibold text-red-700 border-b border-gray-300 w-24 bg-red-50">Avere (EUR)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {editingLines.map((line, idx) => (
                                <tr key={line.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-2 py-1">
                                    <input 
                                      type="text" 
                                      className="w-full border-0 bg-transparent px-1 py-0.5 text-xs font-mono focus:bg-white focus:ring-1 focus:ring-blue-500 rounded" 
                                      value={line.account_code}
                                      onChange={(e) => {
                                        const newLines = [...editingLines];
                                        newLines[idx] = { ...newLines[idx], account_code: e.target.value };
                                        setEditingLines(newLines);
                                      }}
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    <input 
                                      type="text" 
                                      className="w-full border-0 bg-transparent px-1 py-0.5 text-xs focus:bg-white focus:ring-1 focus:ring-blue-500 rounded" 
                                      value={line.account_name}
                                      onChange={(e) => {
                                        const newLines = [...editingLines];
                                        newLines[idx] = { ...newLines[idx], account_name: e.target.value };
                                        setEditingLines(newLines);
                                      }}
                                    />
                                  </td>
                                  <td className="px-2 py-1 bg-green-50/30">
                                    <NumericFormat
                                      value={line.debit_eur ?? line.debit_amount}
                                      thousandSeparator="."
                                      decimalSeparator=","
                                      decimalScale={2}
                                      fixedDecimalScale={true}
                                      className="w-full border-0 bg-transparent px-1 py-0.5 text-xs font-mono focus:bg-white focus:ring-1 focus:ring-blue-500 rounded"
                                      onValueChange={(values) => {
                                        const newDebitEur = values.floatValue ?? 0;
                                        const curr = String(editingHeader?.currency || '').toUpperCase();
                                        const newLines = [...editingLines];
                                        if (curr === 'EUR') {
                                          newLines[idx] = { ...newLines[idx], debit_eur: newDebitEur, debit_amount: newDebitEur };
                                        } else {
                                          const fx = Number(editingHeader?.fx_rate) || 0;
                                          const newDebitAmount = fx > 0 ? Number((newDebitEur * fx).toFixed(2)) : newDebitEur;
                                          newLines[idx] = { ...newLines[idx], debit_eur: newDebitEur, debit_amount: newDebitAmount };
                                        }
                                        setEditingLines(newLines);
                                        if (curr !== 'EUR') {
                                          setEditingHeader((h: any) => ({ ...h, fx_rate_source: 'manuale' }));
                                        }
                                      }}
                                    />
                                  </td>
                                  <td className="px-2 py-1 bg-red-50/30">
                                    <NumericFormat
                                      value={line.credit_eur ?? line.credit_amount}
                                      thousandSeparator="."
                                      decimalSeparator=","
                                      decimalScale={2}
                                      fixedDecimalScale={true}
                                      className="w-full border-0 bg-transparent px-1 py-0.5 text-xs font-mono focus:bg-white focus:ring-1 focus:ring-blue-500 rounded"
                                      onValueChange={(values) => {
                                        const newCreditEur = values.floatValue ?? 0;
                                        const curr = String(editingHeader?.currency || '').toUpperCase();
                                        const newLines = [...editingLines];
                                        if (curr === 'EUR') {
                                          newLines[idx] = { ...newLines[idx], credit_eur: newCreditEur, credit_amount: newCreditEur };
                                        } else {
                                          const fx = Number(editingHeader?.fx_rate) || 0;
                                          const newCreditAmount = fx > 0 ? Number((newCreditEur * fx).toFixed(2)) : newCreditEur;
                                          newLines[idx] = { ...newLines[idx], credit_eur: newCreditEur, credit_amount: newCreditAmount };
                                        }
                                        setEditingLines(newLines);
                                        if (curr !== 'EUR') {
                                          setEditingHeader((h: any) => ({ ...h, fx_rate_source: 'manuale' }));
                                        }
                                      }}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-blue-50 border-t-2 border-blue-300 sticky bottom-0">
                              <tr>
                                <td colSpan={2} className="px-2 py-1.5 text-xs font-bold text-blue-900">TOTALI</td>
                                <td className="px-2 py-1.5 text-right text-xs font-bold text-green-700 tabular-nums">
                                  {formatEUR(editingLines.reduce((sum, l) => sum + ((l.debit_eur ?? l.debit_amount) || 0), 0))}
                                </td>
                                <td className="px-2 py-1.5 text-right text-xs font-bold text-red-700 tabular-nums">
                                  {formatEUR(editingLines.reduce((sum, l) => sum + ((l.credit_eur ?? l.credit_amount) || 0), 0))}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                      
                      {/* Avviso sbilanciamento */}
                      {Math.abs(editingLines.reduce((sum, l) => sum + ((l.debit_eur ?? l.debit_amount) || 0) - ((l.credit_eur ?? l.credit_amount) || 0), 0)) > 0.01 && (
                        <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 flex items-center gap-2">
                          <span className="text-red-600 text-lg">⚠️</span>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-red-800">Scrittura non bilanciata</p>
                            <p className="text-xs text-red-600">
                              Differenza: {formatEUR(Math.abs(editingLines.reduce((sum, l) => sum + ((l.debit_eur ?? l.debit_amount) || 0) - ((l.credit_eur ?? l.credit_amount) || 0), 0)))}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Stato */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Stato scrittura</label>
                      <select 
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs" 
                        value={editing?.status || 'Da processare'} 
                        onChange={(e) => setEditing((s) => s ? ({ ...s, status: e.target.value as EntryStatus }) : s)}
                      >
                        <option>Da processare</option>
                        <option>Registra</option>
                        <option>Confermato</option>
                        <option>Ignora</option>
                      </select>
                    </div>

                    <div className="mt-3 flex justify-end gap-2 sticky bottom-0 bg-white pt-3 border-t border-gray-100">
                      <button 
                        className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs hover:bg-gray-100" 
                        onClick={() => { 
                          setModalOpen(false); 
                          setEditing(null); 
                          setEditingComplete(null);
                          setEditingLines([]);
                        }}
                      >
                        Annulla
                      </button>
                      <button 
                        className="rounded-md border border-blue-600 bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700" 
                        onClick={async () => {
                          try {
                            if (editing?.entryId) {
                              // PATCH di una scrittura esistente con linee
                              const payload = {
                                status: mapStatusLabelToCode(editing.status),
                                date: editingHeader?.date,
                                trade_date: editingHeader?.trade_date,
                                fiscal_year: editingHeader?.fiscal_year,
                                currency: editingHeader?.currency,
                                reason_code: editingHeader?.reason_code,
                                reason_description: editingHeader?.reason_description,
                                ounces: editingHeader?.ounces,
                                note: editingHeader?.note,
                                fx_rate: editingHeader?.fx_rate ?? null,
                                fx_rate_source: editingHeader?.fx_rate_source ?? null,
                                fx_rate_date: editingHeader?.fx_rate_date || null,
                                lines: editingLines.map((line) => ({
                                  id: line.id,
                                  debit_amount: line.debit_amount,
                                  credit_amount: line.credit_amount,
                                  debit_eur: line.debit_eur ?? line.debit_amount,
                                  credit_eur: line.credit_eur ?? line.credit_amount,
                                  account_code: line.account_code ?? line.account,
                                  account_name: line.account_name,
                                }))
                              };
                              await apiFetch(`/api/entries/by-entry-id/${encodeURIComponent(editing.entryId)}`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(payload)
                              });
                            } else if (editing) {
                              // POST manuale usando header + righe
                              const totalDebit = editingLines.reduce((sum, l) => sum + (Number(l.debit_amount) || 0), 0);
                              const totalCredit = editingLines.reduce((sum, l) => sum + (Number(l.credit_amount) || 0), 0);
                              const firstLine = editingLines[0];
                              const payloadManual = {
                                date: editingHeader?.date || editing.date,
                                trade_date: editingHeader?.trade_date || editing.tradeDate || editing.date,
                                fiscal_year: Number(editingHeader?.fiscal_year ?? new Date(editing.date).getFullYear()),
                                note: editingHeader?.note ?? editing.description,
                                currency: editingHeader?.currency || (editing.currency === 'Dollari' ? 'USD' : 'EUR'),
                                reason: editingHeader?.reason_code ?? null,
                                reason_description: editingHeader?.reason_description ?? null,
                                ounces: Number(editingHeader?.ounces ?? 0),
                                account: editingLines.length === 1 ? (firstLine.account_code || null) : null,
                                account_name: editingLines.length === 1 ? (firstLine.account_name || null) : null,
                                debit_amount: totalDebit,
                                credit_amount: totalCredit,
                                status: mapStatusLabelToCode(editing.status),
                              source: editing.source ?? 'manual',
                              bank: editing.bank,
                                fx_rate: editingHeader?.fx_rate ?? null,
                                fx_rate_date: editingHeader?.fx_rate_date || null,
                              };
                              const res = await apiFetch(`/api/entries/manual`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(payloadManual)
                              });
                              if (!res.ok) throw new Error('HTTP ' + res.status);
                            }
                            // Ricarica le entries con i filtri correnti
                            await reloadEntries();
                            setModalOpen(false);
                            setEditing(null);
                            setEditingComplete(null);
                            setEditingLines([]);
                            setEditingHeader(null);
                          } catch (e) {
                            console.error('Errore salvataggio:', e);
                            alert('Errore durante il salvataggio delle modifiche');
                          }
                        }}
                      >
                        Salva
                      </button>
                    </div>
                  </>
                )}

                {!loadingEntry && !editingComplete && editingLines.length === 0 && (
                  <>
                    {/* Modale semplice per entry manuali */}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="block text-xs text-gray-500">Data valuta</label>
                        <input type="date" className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editing?.date || ''} onChange={(e) => setEditing((s) => s ? ({ ...s, date: e.target.value }) : s)} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Trade date</label>
                        <input type="date" className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editing?.tradeDate || ''} onChange={(e) => setEditing((s) => s ? ({ ...s, tradeDate: e.target.value }) : s)} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Valuta</label>
                        <select className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editing?.currency || 'Euro'} onChange={(e) => setEditing((s) => s ? ({ ...s, currency: e.target.value as Currency }) : s)}>
                          <option value="Euro">Euro</option>
                          <option value="Dollari">Dollari</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Tasso di cambio</label>
                        <input type="number" step="0.0001" className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editingHeader?.fx_rate ?? ''} onChange={(e) => setEditingHeader((h: any) => ({ ...h, fx_rate: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Data tasso di cambio</label>
                        <input type="date" className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editingHeader?.fx_rate_date || ''} onChange={(e) => setEditingHeader((h: any) => ({ ...h, fx_rate_date: e.target.value }))} />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500">Descrizione</label>
                        <input type="text" className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editing?.description || ''} onChange={(e) => setEditing((s) => s ? ({ ...s, description: e.target.value }) : s)} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Importo (positivo=DARE, negativo=AVERE)</label>
                        <input type="number" step="0.01" className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editing?.amount ?? 0} onChange={(e) => setEditing((s) => s ? ({ ...s, amount: Number(e.target.value) }) : s)} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Stato</label>
                        <select className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editing?.status || 'Da processare'} onChange={(e) => setEditing((s) => s ? ({ ...s, status: e.target.value as EntryStatus }) : s)}>
                          <option>Da processare</option>
                          <option>Registra</option>
                          <option>Confermato</option>
                          <option>Ignora</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Conto</label>
                        <input type="text" className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editing?.account || ''} onChange={(e) => setEditing((s) => s ? ({ ...s, account_code: e.target.value }) : s)} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Nome conto</label>
                        <input type="text" className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" value={editing?.accountName || ''} onChange={(e) => setEditing((s) => s ? ({ ...s, accountName: e.target.value }) : s)} />
                      </div>
                    </div>
                      <div className="mt-4 flex justify-end gap-2">
                        <button className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm hover:bg-gray-100" onClick={() => { setModalOpen(false); setEditing(null); setEditingLines([]); }}>Annulla</button>
                        <button className="rounded-md border border-blue-600 bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={saveEntry}>Salva</button>
                      </div>
                    </>
                  )}
                </div>
              </Modal>
            </>
          )}
        </main>
      </div>
    </PageShell>
    </>
  );
}
